import { toNumber } from 'lodash-es'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

export interface StiffeningSegmentPlanAxis {
  start: ImmutableVector3
  end: ImmutableVector3
  /**
   * The export's `StiffeningSegmentV2.isXCartesian`: only segments of exactly horizontal walls
   * read horizontally, every other one (diagonal ones included) is rotated by 90°.
   */
  isHorizontal: boolean
}

/**
 * Only the stiffening intervals end up as segments in the stiffening plan (the backend's
 * `StiffeningSegments` rejects everything else); intervals without a guid are unsaved proposals.
 */
export const isPlanStiffeningSegment = (segment: StiffeningSegment): boolean =>
  segment.stiffening === 'Stiffening' && !!segment.guid

/**
 * Axis of a stiffening segment in the plan, like `StiffeningSegmentV2.segmentAxisWorld` in the
 * export: the interval's lower/upper bound measured in metres from the wall axis start. Segments
 * are always drawn on the wall's center axis (`StructuralAxisReference.Center`).
 */
export const getStiffeningSegmentPlanAxis = (
  wall: ShapeObject,
  segment: StiffeningSegment,
): StiffeningSegmentPlanAxis | null => {
  const [p0, p1] = wall.shape.points
  if (!p0 || !p1) return null

  const delta = p1.sub(p0)
  const length = delta.length()
  if (!length) return null

  const direction = delta.divideScalar(length)
  return {
    start: p0.add(direction.multiplyScalar(toNumber(segment.interval.lower))),
    end: p0.add(direction.multiplyScalar(toNumber(segment.interval.upper))),
    isHorizontal: p0.y === p1.y,
  }
}
